// Zustand store

import { createContext, useContext } from "react";
import { createStore, useStore } from "zustand";
import { useShallow } from "zustand/react/shallow";

import { Publication, StorePublication } from "../types/publication";

import {
  createAddFolder,
  createAddLayerToCurrentMap,
  createAddNewMap,
  createAddPublicationFiles,
  createChangeFileName,
  createChangeLayerName,
  createDeleteMap,
  createDeselectAllLayers,
  createMoveMap,
  createPublishVersion,
  createRemoveFileByPath,
  createRemoveFolder,
  createRemoveLayer,
  createRemoveStudyAreaFromCurrentMap,
  createReplaceFile,
  createReplaceLayer,
  createReplaceMap,
  createSelectLayer,
  createSelectLayerRange,
  createSetMapName,
  createSetStudyAreaForCurrentMap,
  createToggleEditing,
  createToggleFolderOpen,
  createToggleLayerSelected,
  createToggleLayerVisibility,
  createToggleStudyAreaVisibilityForCurrentMap,
} from "./mutators";
import {
  createMoveLayersToMap,
  createMoveLayersToPosition,
  createReorderFlatLayers,
} from "./mutators-move";
import { getNewVersionCreateFromPublication } from "./transformers";

export function createPublicationStore(publication: Publication) {
  return createStore<StorePublication>((set, get) => ({
    ...publication,

    _initialPublicationVersionCreate: getNewVersionCreateFromPublication(publication),

    /* Actions to modify the store */
    toggleEditing: createToggleEditing(get, set),
    addLayerToCurrentMap: createAddLayerToCurrentMap(get, set),
    setStudyAreaForCurrentMap: createSetStudyAreaForCurrentMap(get, set),
    removeStudyAreaFromCurrentMap: createRemoveStudyAreaFromCurrentMap(get, set),
    toggleStudyAreaVisibilityForCurrentMap: createToggleStudyAreaVisibilityForCurrentMap(get, set),
    setOpenMap: (id: string) => set({ openMap: id }),
    moveMap: createMoveMap(get, set),
    addNewMap: createAddNewMap(get, set),
    setMapName: createSetMapName(get, set),
    deleteMap: createDeleteMap(get, set),
    replaceMap: createReplaceMap(get, set),
    toggleLayerSelected: createToggleLayerSelected(get),
    selectLayer: createSelectLayer(get),
    selectLayerRange: createSelectLayerRange(get),
    deselectAllLayers: createDeselectAllLayers(get),
    toggleLayerVisibility: createToggleLayerVisibility(get),
    toggleFolderOpen: createToggleFolderOpen(get),
    addFolder: createAddFolder(get),
    removeLayer: createRemoveLayer(get),
    moveLayersToPosition: createMoveLayersToPosition(get),
    reorderFlatLayers: createReorderFlatLayers(get),
    moveLayersToMap: createMoveLayersToMap(get),
    removeFolder: createRemoveFolder(get),
    replaceLayer: createReplaceLayer(get),
    changeLayerName: createChangeLayerName(get),
    addPublicationFiles: createAddPublicationFiles(set),
    replaceFile: createReplaceFile(get, set),
    changeFileName: createChangeFileName(get),
    removeFileByPath: createRemoveFileByPath(get, set),
    publishVersion: createPublishVersion(set),
  }));
}

export type GetFunction = () => StorePublication;
export type SetFunction = {
  (
    partial:
      | StorePublication
      | Partial<StorePublication>
      | ((state: StorePublication) => StorePublication | Partial<StorePublication>),
    replace?: false
  ): void;
  (state: StorePublication | ((state: StorePublication) => StorePublication), replace: true): void;
};
type PublicationStore = ReturnType<typeof createPublicationStore>;

export const PublicationContext = createContext<PublicationStore | null>(null);

export function usePublicationStore<T>(selector: (state: StorePublication) => T): T {
  const publicationStore = useContext(PublicationContext);

  if (publicationStore === null) {
    throw new Error("usePublicationStore used out of PublicationContext.Provider");
  }

  /* Without useShallow, this causes unnecessary re-renders of components that use the store if the selector returns a non-primitive value (e.g. an array or object) that is recreated on every store update. With useShallow, it only causes re-renders if the selected value actually changes. */
  return useStore(publicationStore, useShallow(selector));
}
